import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { perGame } from './insightsClubSummary';
import { exclusionNet, exclusionRatioText } from './insightsPlayerSplits';
import {
    savePctText,
    shotPctText,
    TW_SIT_KEYS,
    TW_SIT_LABELS,
    TwPlayerBoardRow,
    TwPlayerMatchLine,
    TwSitKey,
} from './insightsTwPlayerTables';

export type RateMode = 'totals' | 'perGame';

type SortDir = 1 | -1;

const useSortState = <K extends string>(
    defaultKey: K,
    defaultDir: SortDir = -1
): [K, SortDir, (key: K) => void] => {
    const [key, setKey] = useState<K>(defaultKey);
    const [dir, setDir] = useState<SortDir>(defaultDir);
    const onSort = (next: K): void => {
        if (key === next) {
            setDir((d) => (d === -1 ? 1 : -1));
            return;
        }
        setKey(next);
        setDir(
            next === 'name' ||
                next === 'date' ||
                next === 'opponent' ||
                next === 'result' ||
                next === 'score'
                ? 1
                : -1
        );
    };
    return [key, dir, onSort];
};

const sortMark = (active: boolean, dir: SortDir): string => {
    if (!active) return '';
    return dir === -1 ? ' ↓' : ' ↑';
};

const playerHref = (playerId: number): string =>
    Routes.ROUTE_INSIGHTS_PLAYER.replace(ReplaceStrings.PLAYER_ID, playerId.toString());

const matchHref = (matchId: number): string =>
    Routes.ROUTE_INSIGHTS_MATCH.replace(ReplaceStrings.MATCH_ID, matchId.toString());

const SortTh: React.FC<{
    label: string;
    active: boolean;
    dir: SortDir;
    onClick: () => void;
    className?: string;
}> = ({ label, active, dir, onClick, className }) => (
    <th className={className}>
        <button type="button" className="insightsSortTh" onClick={onClick}>
            {label}
            {sortMark(active, dir)}
        </button>
    </th>
);

type SitMetric = 'goals' | 'assists' | 'pct' | 'saves' | 'attempts' | 'savePct' | 'pg';

const totalsCell = (total: number): string => String(total);

/** Shared cell formatter for Totals | Per game on roster boards. */
export const makeRateCell =
    (rateMode: RateMode) =>
    (total: number, matches: number): string =>
        rateMode === 'perGame' ? perGame(total, matches) : String(total);

interface RosterBoardProps {
    rows: TwPlayerBoardRow[];
    cell: (total: number, matches: number) => string;
    rateMode: RateMode;
}

interface MatchBoardProps {
    lines: TwPlayerMatchLine[];
}

const formatLineScore = (line: TwPlayerMatchLine): string => {
    if (line.goalsFor == null || line.goalsAgainst == null) return '-';
    return `${line.goalsFor}:${line.goalsAgainst}`;
};

const resultCmp = (a: TwPlayerMatchLine, b: TwPlayerMatchLine): number =>
    (a.result || '').localeCompare(b.result || '');

const scoreCmp = (a: TwPlayerMatchLine, b: TwPlayerMatchLine): number => {
    const ag = (a.goalsFor ?? 0) - (a.goalsAgainst ?? 0);
    const bg = (b.goalsFor ?? 0) - (b.goalsAgainst ?? 0);
    if (ag !== bg) return ag - bg;
    return (a.goalsFor ?? 0) - (b.goalsFor ?? 0);
};

const shotMetricCmp = (
    ba: TwPlayerBoardRow['shots'][TwSitKey],
    bb: TwPlayerBoardRow['shots'][TwSitKey],
    metric: SitMetric,
    matchesA: number,
    matchesB: number
): number => {
    if (metric === 'goals') return ba.goals - bb.goals;
    if (metric === 'attempts') return ba.attempts - bb.attempts;
    if (metric === 'assists') return ba.assists - bb.assists;
    if (metric === 'pct') {
        const pa = ba.attempts > 0 ? ba.goals / ba.attempts : -1;
        const pb = bb.attempts > 0 ? bb.goals / bb.attempts : -1;
        return pa - pb;
    }
    if (metric === 'pg') {
        return (matchesA > 0 ? ba.goals / matchesA : 0) - (matchesB > 0 ? bb.goals / matchesB : 0);
    }
    return 0;
};

const saveMetricCmp = (
    ba: TwPlayerBoardRow['saves'][TwSitKey],
    bb: TwPlayerBoardRow['saves'][TwSitKey],
    metric: SitMetric,
    matchesA: number,
    matchesB: number
): number => {
    if (metric === 'saves') return ba.saves - bb.saves;
    if (metric === 'attempts') return ba.attempts - bb.attempts;
    if (metric === 'savePct') {
        const pa = ba.attempts > 0 ? ba.saves / ba.attempts : -1;
        const pb = bb.attempts > 0 ? bb.saves / bb.attempts : -1;
        return pa - pb;
    }
    if (metric === 'pg') {
        return (matchesA > 0 ? ba.saves / matchesA : 0) - (matchesB > 0 ? bb.saves / matchesB : 0);
    }
    return 0;
};

const ScorersMetricHeads: React.FC<{
    rateMode: RateMode;
    sortKey: string;
    sortDir: SortDir;
    onSort: (key: string) => void;
    leadPads: number;
}> = ({ rateMode, sortKey, sortDir, onSort, leadPads }) => (
    <>
        <tr>
            {Array.from({ length: leadPads }).map((_, i) => (
                <th key={`pad-${i}`} />
            ))}
            {TW_SIT_KEYS.map((sit) => (
                <React.Fragment key={sit}>
                    <SortTh
                        className="insightsTwSitLead"
                        label="G"
                        active={sortKey === `${sit}:goals`}
                        dir={sortDir}
                        onClick={() => onSort(`${sit}:goals`)}
                    />
                    <SortTh
                        label="Att"
                        active={sortKey === `${sit}:attempts`}
                        dir={sortDir}
                        onClick={() => onSort(`${sit}:attempts`)}
                    />
                    <SortTh
                        label="A"
                        active={sortKey === `${sit}:assists`}
                        dir={sortDir}
                        onClick={() => onSort(`${sit}:assists`)}
                    />
                    <SortTh
                        label="%"
                        active={sortKey === `${sit}:pct`}
                        dir={sortDir}
                        onClick={() => onSort(`${sit}:pct`)}
                    />
                    {rateMode === 'perGame' ? (
                        <SortTh
                            label="P/G"
                            active={sortKey === `${sit}:pg`}
                            dir={sortDir}
                            onClick={() => onSort(`${sit}:pg`)}
                        />
                    ) : null}
                </React.Fragment>
            ))}
        </tr>
    </>
);

const ScorersMetricCells: React.FC<{
    board: TwPlayerBoardRow;
    cell: (total: number, matches: number) => string;
    rateMode: RateMode;
}> = ({ board, cell, rateMode }) => (
    <>
        {TW_SIT_KEYS.map((sit: TwSitKey) => {
            const b = board.shots[sit];
            return (
                <React.Fragment key={sit}>
                    <td className="insightsTwSitLead">{cell(b.goals, board.matches)}</td>
                    <td>{cell(b.attempts, board.matches)}</td>
                    <td>{cell(b.assists, board.matches)}</td>
                    <td>{shotPctText(b.goals, b.attempts)}</td>
                    {rateMode === 'perGame' ? <td>{perGame(b.goals, board.matches)}</td> : null}
                </React.Fragment>
            );
        })}
    </>
);

export const TwScorersBoard: React.FC<RosterBoardProps & { heading?: string }> = ({
    rows,
    cell,
    rateMode,
    heading = 'Scorers',
}) => {
    type Key = 'name' | 'matches' | `${TwSitKey}:${SitMetric}`;
    const [sortKey, sortDir, onSort] = useSortState<Key>('total:goals');

    const sorted = useMemo(() => {
        const list = [...rows];
        list.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
            else if (sortKey === 'matches') cmp = a.matches - b.matches;
            else {
                const [sit, metric] = sortKey.split(':') as [TwSitKey, SitMetric];
                cmp = shotMetricCmp(a.shots[sit], b.shots[sit], metric, a.matches, b.matches);
            }
            if (cmp !== 0) return cmp * sortDir;
            return a.name.localeCompare(b.name);
        });
        return list;
    }, [rows, sortKey, sortDir]);

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>{heading}</h2>
                <span>Σ / 6v6 / PP / Pen / FB · G / Att / A / %</span>
            </div>
            {rows.length === 0 ? (
                <div className="insightsEmpty">No shooting events in this filter.</div>
            ) : (
                <div className="insightsTableScroll">
                    <table className="insightsPlayerTable insightsPlayerTableWide insightsTwTable">
                        <thead>
                            <tr>
                                <SortTh
                                    label="Player"
                                    active={sortKey === 'name'}
                                    dir={sortDir}
                                    onClick={() => onSort('name')}
                                />
                                <SortTh
                                    label="MP"
                                    active={sortKey === 'matches'}
                                    dir={sortDir}
                                    onClick={() => onSort('matches')}
                                />
                                {TW_SIT_KEYS.map((sit) => (
                                    <th key={sit} colSpan={rateMode === 'totals' ? 4 : 5}>
                                        {TW_SIT_LABELS[sit]}
                                    </th>
                                ))}
                            </tr>
                            <ScorersMetricHeads
                                rateMode={rateMode}
                                sortKey={sortKey}
                                sortDir={sortDir}
                                onSort={(k) => onSort(k as Key)}
                                leadPads={2}
                            />
                        </thead>
                        <tbody>
                            {sorted.map((row) => (
                                <tr key={row.playerId}>
                                    <td>
                                        <Link
                                            className="insightsMatchLink"
                                            to={playerHref(row.playerId)}
                                        >
                                            {row.name}
                                        </Link>
                                    </td>
                                    <td>{row.matches}</td>
                                    <ScorersMetricCells
                                        board={row}
                                        cell={cell}
                                        rateMode={rateMode}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export const TwMatchScorersBoard: React.FC<MatchBoardProps> = ({ lines }) => {
    type Key = 'date' | 'opponent' | 'result' | 'score' | `${TwSitKey}:${SitMetric}`;
    const [sortKey, sortDir, onSort] = useSortState<Key>('date', -1);

    const sorted = useMemo(() => {
        const list = [...lines];
        list.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'date') {
                cmp =
                    (a.startDate ? new Date(a.startDate).getTime() : 0) -
                    (b.startDate ? new Date(b.startDate).getTime() : 0);
            } else if (sortKey === 'opponent') {
                cmp = a.opponentName.localeCompare(b.opponentName);
            } else if (sortKey === 'result') {
                cmp = resultCmp(a, b);
            } else if (sortKey === 'score') {
                cmp = scoreCmp(a, b);
            } else {
                const [sit, metric] = sortKey.split(':') as [TwSitKey, SitMetric];
                cmp = shotMetricCmp(
                    a.board.shots[sit],
                    b.board.shots[sit],
                    metric,
                    a.board.matches,
                    b.board.matches
                );
            }
            if (cmp !== 0) return cmp * sortDir;
            return a.opponentName.localeCompare(b.opponentName);
        });
        return list;
    }, [lines, sortKey, sortDir]);

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Match · Scorers</h2>
                <span>G / Att / A / %</span>
            </div>
            {lines.length === 0 ? (
                <div className="insightsEmpty">No appearances in the filtered matches.</div>
            ) : (
                <div className="insightsTableScroll">
                    <table className="insightsPlayerTable insightsPlayerTableWide insightsTwTable">
                        <thead>
                            <tr>
                                <SortTh
                                    label="Date"
                                    active={sortKey === 'date'}
                                    dir={sortDir}
                                    onClick={() => onSort('date')}
                                />
                                <SortTh
                                    label="Opponent"
                                    active={sortKey === 'opponent'}
                                    dir={sortDir}
                                    onClick={() => onSort('opponent')}
                                />
                                <SortTh
                                    label="Res"
                                    active={sortKey === 'result'}
                                    dir={sortDir}
                                    onClick={() => onSort('result')}
                                />
                                <SortTh
                                    label="Score"
                                    active={sortKey === 'score'}
                                    dir={sortDir}
                                    onClick={() => onSort('score')}
                                />
                                {TW_SIT_KEYS.map((sit) => (
                                    <th key={sit} colSpan={4}>
                                        {TW_SIT_LABELS[sit]}
                                    </th>
                                ))}
                            </tr>
                            <ScorersMetricHeads
                                rateMode="totals"
                                sortKey={sortKey}
                                sortDir={sortDir}
                                onSort={(k) => onSort(k as Key)}
                                leadPads={4}
                            />
                        </thead>
                        <tbody>
                            {sorted.map((line) => (
                                <tr key={line.matchId}>
                                    <td>
                                        {line.startDate
                                            ? moment(line.startDate).format('DD MMM YY')
                                            : '-'}
                                    </td>
                                    <td>
                                        <Link
                                            className="insightsMatchLink"
                                            to={matchHref(line.matchId)}
                                        >
                                            {line.isHome ? 'vs' : 'at'} {line.opponentName}
                                        </Link>
                                    </td>
                                    <td>{line.result || '-'}</td>
                                    <td>{formatLineScore(line)}</td>
                                    <ScorersMetricCells
                                        board={line.board}
                                        cell={totalsCell}
                                        rateMode="totals"
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

const SavesMetricHeads: React.FC<{
    rateMode: RateMode;
    sortKey: string;
    sortDir: SortDir;
    onSort: (key: string) => void;
    leadPads: number;
}> = ({ rateMode, sortKey, sortDir, onSort, leadPads }) => (
    <tr>
        {Array.from({ length: leadPads }).map((_, i) => (
            <th key={`pad-${i}`} />
        ))}
        {TW_SIT_KEYS.map((sit) => (
            <React.Fragment key={sit}>
                <SortTh
                    className="insightsTwSitLead"
                    label="S"
                    active={sortKey === `${sit}:saves`}
                    dir={sortDir}
                    onClick={() => onSort(`${sit}:saves`)}
                />
                <SortTh
                    label="Att"
                    active={sortKey === `${sit}:attempts`}
                    dir={sortDir}
                    onClick={() => onSort(`${sit}:attempts`)}
                />
                <SortTh
                    label="%"
                    active={sortKey === `${sit}:savePct`}
                    dir={sortDir}
                    onClick={() => onSort(`${sit}:savePct`)}
                />
                {rateMode === 'perGame' ? (
                    <SortTh
                        label="P/G"
                        active={sortKey === `${sit}:pg`}
                        dir={sortDir}
                        onClick={() => onSort(`${sit}:pg`)}
                    />
                ) : null}
            </React.Fragment>
        ))}
    </tr>
);

const SavesMetricCells: React.FC<{
    board: TwPlayerBoardRow;
    cell: (total: number, matches: number) => string;
    rateMode: RateMode;
}> = ({ board, cell, rateMode }) => (
    <>
        {TW_SIT_KEYS.map((sit: TwSitKey) => {
            const b = board.saves[sit];
            return (
                <React.Fragment key={sit}>
                    <td className="insightsTwSitLead">{cell(b.saves, board.matches)}</td>
                    <td>{cell(b.attempts, board.matches)}</td>
                    <td>{savePctText(b.saves, b.attempts)}</td>
                    {rateMode === 'perGame' ? <td>{perGame(b.saves, board.matches)}</td> : null}
                </React.Fragment>
            );
        })}
    </>
);

export const TwSavesBoard: React.FC<RosterBoardProps & { heading?: string }> = ({
    rows,
    cell,
    rateMode,
    heading = 'Saves',
}) => {
    type Key = 'name' | 'matches' | `${TwSitKey}:${SitMetric}`;
    const [sortKey, sortDir, onSort] = useSortState<Key>('total:saves');

    const sorted = useMemo(() => {
        const list = [...rows];
        list.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
            else if (sortKey === 'matches') cmp = a.matches - b.matches;
            else {
                const [sit, metric] = sortKey.split(':') as [TwSitKey, SitMetric];
                cmp = saveMetricCmp(a.saves[sit], b.saves[sit], metric, a.matches, b.matches);
            }
            if (cmp !== 0) return cmp * sortDir;
            return a.name.localeCompare(b.name);
        });
        return list;
    }, [rows, sortKey, sortDir]);

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>{heading}</h2>
                <span>Σ / 6v6 / PP / Pen / FB · Att = SOG faced</span>
            </div>
            {rows.length === 0 ? (
                <div className="insightsEmpty">No save / goals-against events in this filter.</div>
            ) : (
                <div className="insightsTableScroll">
                    <table className="insightsPlayerTable insightsPlayerTableWide insightsTwTable">
                        <thead>
                            <tr>
                                <SortTh
                                    label="Player"
                                    active={sortKey === 'name'}
                                    dir={sortDir}
                                    onClick={() => onSort('name')}
                                />
                                <SortTh
                                    label="MP"
                                    active={sortKey === 'matches'}
                                    dir={sortDir}
                                    onClick={() => onSort('matches')}
                                />
                                {TW_SIT_KEYS.map((sit) => (
                                    <th key={sit} colSpan={rateMode === 'totals' ? 3 : 4}>
                                        {TW_SIT_LABELS[sit]}
                                    </th>
                                ))}
                            </tr>
                            <SavesMetricHeads
                                rateMode={rateMode}
                                sortKey={sortKey}
                                sortDir={sortDir}
                                onSort={(k) => onSort(k as Key)}
                                leadPads={2}
                            />
                        </thead>
                        <tbody>
                            {sorted.map((row) => (
                                <tr key={row.playerId}>
                                    <td>
                                        <Link
                                            className="insightsMatchLink"
                                            to={playerHref(row.playerId)}
                                        >
                                            {row.name}
                                        </Link>
                                    </td>
                                    <td>{row.matches}</td>
                                    <SavesMetricCells board={row} cell={cell} rateMode={rateMode} />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export const TwMatchSavesBoard: React.FC<MatchBoardProps> = ({ lines }) => {
    type Key = 'date' | 'opponent' | 'result' | 'score' | `${TwSitKey}:${SitMetric}`;
    const [sortKey, sortDir, onSort] = useSortState<Key>('date', -1);

    const sorted = useMemo(() => {
        const list = [...lines];
        list.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'date') {
                cmp =
                    (a.startDate ? new Date(a.startDate).getTime() : 0) -
                    (b.startDate ? new Date(b.startDate).getTime() : 0);
            } else if (sortKey === 'opponent') {
                cmp = a.opponentName.localeCompare(b.opponentName);
            } else if (sortKey === 'result') {
                cmp = resultCmp(a, b);
            } else if (sortKey === 'score') {
                cmp = scoreCmp(a, b);
            } else {
                const [sit, metric] = sortKey.split(':') as [TwSitKey, SitMetric];
                cmp = saveMetricCmp(
                    a.board.saves[sit],
                    b.board.saves[sit],
                    metric,
                    a.board.matches,
                    b.board.matches
                );
            }
            if (cmp !== 0) return cmp * sortDir;
            return a.opponentName.localeCompare(b.opponentName);
        });
        return list;
    }, [lines, sortKey, sortDir]);

    if (lines.length === 0) return null;

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Match · Saves</h2>
                <span>Att = SOG faced</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsTwTable">
                    <thead>
                        <tr>
                            <SortTh
                                label="Date"
                                active={sortKey === 'date'}
                                dir={sortDir}
                                onClick={() => onSort('date')}
                            />
                            <SortTh
                                label="Opponent"
                                active={sortKey === 'opponent'}
                                dir={sortDir}
                                onClick={() => onSort('opponent')}
                            />
                            <SortTh
                                label="Res"
                                active={sortKey === 'result'}
                                dir={sortDir}
                                onClick={() => onSort('result')}
                            />
                            <SortTh
                                label="Score"
                                active={sortKey === 'score'}
                                dir={sortDir}
                                onClick={() => onSort('score')}
                            />
                            {TW_SIT_KEYS.map((sit) => (
                                <th key={sit} colSpan={3}>
                                    {TW_SIT_LABELS[sit]}
                                </th>
                            ))}
                        </tr>
                        <SavesMetricHeads
                            rateMode="totals"
                            sortKey={sortKey}
                            sortDir={sortDir}
                            onSort={(k) => onSort(k as Key)}
                            leadPads={4}
                        />
                    </thead>
                    <tbody>
                        {sorted.map((line) => (
                            <tr key={line.matchId}>
                                <td>
                                    {line.startDate
                                        ? moment(line.startDate).format('DD MMM YY')
                                        : '-'}
                                </td>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={matchHref(line.matchId)}
                                    >
                                        {line.isHome ? 'vs' : 'at'} {line.opponentName}
                                    </Link>
                                </td>
                                <td>{line.result || '-'}</td>
                                <td>{formatLineScore(line)}</td>
                                <SavesMetricCells
                                    board={line.board}
                                    cell={totalsCell}
                                    rateMode="totals"
                                />
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

type SupportKey = 'name' | 'matches' | 'assists' | 'blocks' | 'steals' | 'turnovers';

type SupportExKey = SupportKey | 'exD' | 'exC' | 'exNet' | 'exRatio';

const compareSupportEx = (
    a: TwPlayerBoardRow,
    b: TwPlayerBoardRow,
    sortKey: SupportExKey
): number => {
    if (sortKey === 'name') return a.name.localeCompare(b.name);
    if (sortKey === 'matches') return a.matches - b.matches;
    if (sortKey === 'assists') return a.assists - b.assists;
    if (sortKey === 'blocks') return a.blocks - b.blocks;
    if (sortKey === 'steals') return a.steals - b.steals;
    if (sortKey === 'turnovers') return a.turnovers - b.turnovers;
    if (sortKey === 'exD') return a.exclusionsDrawn - b.exclusionsDrawn;
    if (sortKey === 'exC') return a.exclusionsCommitted - b.exclusionsCommitted;
    if (sortKey === 'exNet') {
        return (
            exclusionNet(a.exclusionsDrawn, a.exclusionsCommitted) -
            exclusionNet(b.exclusionsDrawn, b.exclusionsCommitted)
        );
    }
    const ra =
        a.exclusionsCommitted > 0 ? a.exclusionsDrawn / a.exclusionsCommitted : a.exclusionsDrawn;
    const rb =
        b.exclusionsCommitted > 0 ? b.exclusionsDrawn / b.exclusionsCommitted : b.exclusionsDrawn;
    return ra - rb;
};

/**
 * Roster board: Cap / Player / MP once, then support metrics, thick divider, then exclusions.
 * Cap column dropped; Player / MP remain the identity cols.
 * Prefer this over separate TwSupportBoard + TwExclusionsBoard on list pages.
 */
export const TwSupportExclusionsBoard: React.FC<{
    rows: TwPlayerBoardRow[];
    cell: (total: number, matches: number) => string;
}> = ({ rows, cell }) => {
    const [sortKey, sortDir, onSort] = useSortState<SupportExKey>('assists');
    if (rows.length === 0) return null;

    const sorted = [...rows].sort((a, b) => {
        const cmp = compareSupportEx(a, b, sortKey);
        if (cmp !== 0) return cmp * sortDir;
        return a.name.localeCompare(b.name);
    });

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Support · Exclusions</h2>
                <span>As · Bl · St · TO · ExD · ExC · Net · D/C</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable insightsSupportExTable">
                    <thead>
                        <tr>
                            <th rowSpan={2}>
                                <button
                                    type="button"
                                    className="insightsSortTh"
                                    onClick={() => onSort('name')}
                                >
                                    Player{sortMark(sortKey === 'name', sortDir)}
                                </button>
                            </th>
                            <th rowSpan={2}>
                                <button
                                    type="button"
                                    className="insightsSortTh"
                                    onClick={() => onSort('matches')}
                                >
                                    MP{sortMark(sortKey === 'matches', sortDir)}
                                </button>
                            </th>
                            <th colSpan={4}>Support</th>
                            <th className="insightsColDivider" colSpan={4}>
                                Exclusions
                            </th>
                        </tr>
                        <tr>
                            <SortTh
                                label="As"
                                active={sortKey === 'assists'}
                                dir={sortDir}
                                onClick={() => onSort('assists')}
                            />
                            <SortTh
                                label="Bl"
                                active={sortKey === 'blocks'}
                                dir={sortDir}
                                onClick={() => onSort('blocks')}
                            />
                            <SortTh
                                label="St"
                                active={sortKey === 'steals'}
                                dir={sortDir}
                                onClick={() => onSort('steals')}
                            />
                            <SortTh
                                label="TO"
                                active={sortKey === 'turnovers'}
                                dir={sortDir}
                                onClick={() => onSort('turnovers')}
                            />
                            <SortTh
                                label="ExD"
                                active={sortKey === 'exD'}
                                dir={sortDir}
                                onClick={() => onSort('exD')}
                                className="insightsColDivider"
                            />
                            <SortTh
                                label="ExC"
                                active={sortKey === 'exC'}
                                dir={sortDir}
                                onClick={() => onSort('exC')}
                            />
                            <SortTh
                                label="Net"
                                active={sortKey === 'exNet'}
                                dir={sortDir}
                                onClick={() => onSort('exNet')}
                            />
                            <SortTh
                                label="D/C"
                                active={sortKey === 'exRatio'}
                                dir={sortDir}
                                onClick={() => onSort('exRatio')}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((row) => (
                            <tr key={row.playerId}>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={playerHref(row.playerId)}
                                    >
                                        {row.name}
                                    </Link>
                                </td>
                                <td>{row.matches}</td>
                                <td>{cell(row.assists, row.matches)}</td>
                                <td>{cell(row.blocks, row.matches)}</td>
                                <td>{cell(row.steals, row.matches)}</td>
                                <td>{cell(row.turnovers, row.matches)}</td>
                                <td className="insightsColDivider">
                                    {cell(row.exclusionsDrawn, row.matches)}
                                </td>
                                <td>{cell(row.exclusionsCommitted, row.matches)}</td>
                                <td>
                                    {exclusionNet(row.exclusionsDrawn, row.exclusionsCommitted)}
                                </td>
                                <td>
                                    {exclusionRatioText(
                                        row.exclusionsDrawn,
                                        row.exclusionsCommitted
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export const TwSupportBoard: React.FC<{
    rows: TwPlayerBoardRow[];
    cell: (total: number, matches: number) => string;
}> = ({ rows, cell }) => {
    const [sortKey, sortDir, onSort] = useSortState<SupportKey>('assists');
    if (rows.length === 0) return null;

    const sorted = [...rows].sort((a, b) => {
        let cmp = 0;
        if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
        else if (sortKey === 'matches') cmp = a.matches - b.matches;
        else if (sortKey === 'assists') cmp = a.assists - b.assists;
        else if (sortKey === 'blocks') cmp = a.blocks - b.blocks;
        else if (sortKey === 'steals') cmp = a.steals - b.steals;
        else cmp = a.turnovers - b.turnovers;
        if (cmp !== 0) return cmp * sortDir;
        return a.name.localeCompare(b.name);
    });

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Assists · Blocks · Steals · Turnovers</h2>
                <span>As · Bl · St · TO</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable">
                    <thead>
                        <tr>
                            <SortTh
                                label="Player"
                                active={sortKey === 'name'}
                                dir={sortDir}
                                onClick={() => onSort('name')}
                            />
                            <SortTh
                                label="MP"
                                active={sortKey === 'matches'}
                                dir={sortDir}
                                onClick={() => onSort('matches')}
                            />
                            <SortTh
                                label="As"
                                active={sortKey === 'assists'}
                                dir={sortDir}
                                onClick={() => onSort('assists')}
                            />
                            <SortTh
                                label="Bl"
                                active={sortKey === 'blocks'}
                                dir={sortDir}
                                onClick={() => onSort('blocks')}
                            />
                            <SortTh
                                label="St"
                                active={sortKey === 'steals'}
                                dir={sortDir}
                                onClick={() => onSort('steals')}
                            />
                            <SortTh
                                label="TO"
                                active={sortKey === 'turnovers'}
                                dir={sortDir}
                                onClick={() => onSort('turnovers')}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((row) => (
                            <tr key={row.playerId}>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={playerHref(row.playerId)}
                                    >
                                        {row.name}
                                    </Link>
                                </td>
                                <td>{row.matches}</td>
                                <td>{cell(row.assists, row.matches)}</td>
                                <td>{cell(row.blocks, row.matches)}</td>
                                <td>{cell(row.steals, row.matches)}</td>
                                <td>{cell(row.turnovers, row.matches)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

type OtherKey =
    | 'name'
    | 'matches'
    | 'assists'
    | 'blocks'
    | 'steals'
    | 'turnovers'
    | 'exD'
    | 'exC';

/** Assists / blocks / steals / turnovers / exclusions - one board for match box-score "everything else". */
export const TwOtherBoard: React.FC<{
    rows: TwPlayerBoardRow[];
    cell: (total: number, matches: number) => string;
}> = ({ rows, cell }) => {
    const [sortKey, sortDir, onSort] = useSortState<OtherKey>('assists');

    const sorted = useMemo(() => {
        const list = [...rows];
        list.sort((a, b) => {
            let cmp = 0;
            if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
            else if (sortKey === 'matches') cmp = a.matches - b.matches;
            else if (sortKey === 'assists') cmp = a.assists - b.assists;
            else if (sortKey === 'blocks') cmp = a.blocks - b.blocks;
            else if (sortKey === 'steals') cmp = a.steals - b.steals;
            else if (sortKey === 'turnovers') cmp = a.turnovers - b.turnovers;
            else if (sortKey === 'exD') cmp = a.exclusionsDrawn - b.exclusionsDrawn;
            else cmp = a.exclusionsCommitted - b.exclusionsCommitted;
            if (cmp !== 0) return cmp * sortDir;
            return a.name.localeCompare(b.name);
        });
        return list;
    }, [rows, sortKey, sortDir]);

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Other</h2>
                <span>As · Bl · St · TO · ExD · ExC</span>
            </div>
            {rows.length === 0 ? (
                <div className="insightsEmpty">
                    No assists, blocks, steals, turnovers or exclusions.
                </div>
            ) : (
                <div className="insightsTableScroll">
                    <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable">
                        <thead>
                            <tr>
                                <SortTh
                                    label="Player"
                                    active={sortKey === 'name'}
                                    dir={sortDir}
                                    onClick={() => onSort('name')}
                                />
                                <SortTh
                                    label="MP"
                                    active={sortKey === 'matches'}
                                    dir={sortDir}
                                    onClick={() => onSort('matches')}
                                />
                                <SortTh
                                    label="As"
                                    active={sortKey === 'assists'}
                                    dir={sortDir}
                                    onClick={() => onSort('assists')}
                                />
                                <SortTh
                                    label="Bl"
                                    active={sortKey === 'blocks'}
                                    dir={sortDir}
                                    onClick={() => onSort('blocks')}
                                />
                                <SortTh
                                    label="St"
                                    active={sortKey === 'steals'}
                                    dir={sortDir}
                                    onClick={() => onSort('steals')}
                                />
                                <SortTh
                                    label="TO"
                                    active={sortKey === 'turnovers'}
                                    dir={sortDir}
                                    onClick={() => onSort('turnovers')}
                                />
                                <SortTh
                                    label="ExD"
                                    active={sortKey === 'exD'}
                                    dir={sortDir}
                                    onClick={() => onSort('exD')}
                                />
                                <SortTh
                                    label="ExC"
                                    active={sortKey === 'exC'}
                                    dir={sortDir}
                                    onClick={() => onSort('exC')}
                                />
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((row) => (
                                <tr key={row.playerId}>
                                    <td>
                                        <Link
                                            className="insightsMatchLink"
                                            to={playerHref(row.playerId)}
                                        >
                                            {row.name}
                                        </Link>
                                    </td>
                                    <td>{row.matches}</td>
                                    <td>{cell(row.assists, row.matches)}</td>
                                    <td>{cell(row.blocks, row.matches)}</td>
                                    <td>{cell(row.steals, row.matches)}</td>
                                    <td>{cell(row.turnovers, row.matches)}</td>
                                    <td>{cell(row.exclusionsDrawn, row.matches)}</td>
                                    <td>{cell(row.exclusionsCommitted, row.matches)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

type MatchSupportKey =
    | 'date'
    | 'opponent'
    | 'result'
    | 'score'
    | 'assists'
    | 'blocks'
    | 'steals'
    | 'turnovers';

export const TwMatchSupportBoard: React.FC<MatchBoardProps> = ({ lines }) => {
    const [sortKey, sortDir, onSort] = useSortState<MatchSupportKey>('date', -1);
    if (lines.length === 0) return null;

    const sorted = [...lines].sort((a, b) => {
        let cmp = 0;
        if (sortKey === 'date') {
            cmp =
                (a.startDate ? new Date(a.startDate).getTime() : 0) -
                (b.startDate ? new Date(b.startDate).getTime() : 0);
        } else if (sortKey === 'opponent') cmp = a.opponentName.localeCompare(b.opponentName);
        else if (sortKey === 'result') cmp = resultCmp(a, b);
        else if (sortKey === 'score') cmp = scoreCmp(a, b);
        else if (sortKey === 'assists') cmp = a.board.assists - b.board.assists;
        else if (sortKey === 'blocks') cmp = a.board.blocks - b.board.blocks;
        else if (sortKey === 'steals') cmp = a.board.steals - b.board.steals;
        else cmp = a.board.turnovers - b.board.turnovers;
        if (cmp !== 0) return cmp * sortDir;
        return a.opponentName.localeCompare(b.opponentName);
    });

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Match · Assists · Blocks · Steals · Turnovers</h2>
                <span>As · Bl · St · TO</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable">
                    <thead>
                        <tr>
                            <SortTh
                                label="Date"
                                active={sortKey === 'date'}
                                dir={sortDir}
                                onClick={() => onSort('date')}
                            />
                            <SortTh
                                label="Opponent"
                                active={sortKey === 'opponent'}
                                dir={sortDir}
                                onClick={() => onSort('opponent')}
                            />
                            <SortTh
                                label="Res"
                                active={sortKey === 'result'}
                                dir={sortDir}
                                onClick={() => onSort('result')}
                            />
                            <SortTh
                                label="Score"
                                active={sortKey === 'score'}
                                dir={sortDir}
                                onClick={() => onSort('score')}
                            />
                            <SortTh
                                label="As"
                                active={sortKey === 'assists'}
                                dir={sortDir}
                                onClick={() => onSort('assists')}
                            />
                            <SortTh
                                label="Bl"
                                active={sortKey === 'blocks'}
                                dir={sortDir}
                                onClick={() => onSort('blocks')}
                            />
                            <SortTh
                                label="St"
                                active={sortKey === 'steals'}
                                dir={sortDir}
                                onClick={() => onSort('steals')}
                            />
                            <SortTh
                                label="TO"
                                active={sortKey === 'turnovers'}
                                dir={sortDir}
                                onClick={() => onSort('turnovers')}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((line) => (
                            <tr key={line.matchId}>
                                <td>
                                    {line.startDate
                                        ? moment(line.startDate).format('DD MMM YY')
                                        : '-'}
                                </td>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={matchHref(line.matchId)}
                                    >
                                        {line.isHome ? 'vs' : 'at'} {line.opponentName}
                                    </Link>
                                </td>
                                <td>{line.result || '-'}</td>
                                <td>{formatLineScore(line)}</td>
                                <td>{line.board.assists}</td>
                                <td>{line.board.blocks}</td>
                                <td>{line.board.steals}</td>
                                <td>{line.board.turnovers}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

type MatchSupportExKey =
    | 'date'
    | 'opponent'
    | 'result'
    | 'score'
    | 'assists'
    | 'blocks'
    | 'steals'
    | 'turnovers'
    | 'exD'
    | 'exC'
    | 'exNet'
    | 'exRatio';

const compareMatchSupportEx = (
    a: TwPlayerMatchLine,
    b: TwPlayerMatchLine,
    sortKey: MatchSupportExKey
): number => {
    if (sortKey === 'date') {
        return (
            (a.startDate ? new Date(a.startDate).getTime() : 0) -
            (b.startDate ? new Date(b.startDate).getTime() : 0)
        );
    }
    if (sortKey === 'opponent') return a.opponentName.localeCompare(b.opponentName);
    if (sortKey === 'result') return resultCmp(a, b);
    if (sortKey === 'score') return scoreCmp(a, b);
    if (sortKey === 'assists') return a.board.assists - b.board.assists;
    if (sortKey === 'blocks') return a.board.blocks - b.board.blocks;
    if (sortKey === 'steals') return a.board.steals - b.board.steals;
    if (sortKey === 'turnovers') return a.board.turnovers - b.board.turnovers;
    if (sortKey === 'exD') return a.board.exclusionsDrawn - b.board.exclusionsDrawn;
    if (sortKey === 'exC') return a.board.exclusionsCommitted - b.board.exclusionsCommitted;
    if (sortKey === 'exNet') {
        return (
            exclusionNet(a.board.exclusionsDrawn, a.board.exclusionsCommitted) -
            exclusionNet(b.board.exclusionsDrawn, b.board.exclusionsCommitted)
        );
    }
    const ra =
        a.board.exclusionsCommitted > 0
            ? a.board.exclusionsDrawn / a.board.exclusionsCommitted
            : a.board.exclusionsDrawn;
    const rb =
        b.board.exclusionsCommitted > 0
            ? b.board.exclusionsDrawn / b.board.exclusionsCommitted
            : b.board.exclusionsDrawn;
    return ra - rb;
};

/** Match-by-match: Date / Opponent / Res / Score once, then support, divider, exclusions. */
export const TwMatchSupportExclusionsBoard: React.FC<MatchBoardProps> = ({ lines }) => {
    const [sortKey, sortDir, onSort] = useSortState<MatchSupportExKey>('date', -1);
    if (lines.length === 0) return null;

    const sorted = [...lines].sort((a, b) => {
        const cmp = compareMatchSupportEx(a, b, sortKey);
        if (cmp !== 0) return cmp * sortDir;
        return a.opponentName.localeCompare(b.opponentName);
    });

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Match · Support · Exclusions</h2>
                <span>As · Bl · St · TO · ExD · ExC · Net · D/C</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable insightsSupportExTable">
                    <thead>
                        <tr>
                            <th rowSpan={2}>
                                <button
                                    type="button"
                                    className="insightsSortTh"
                                    onClick={() => onSort('date')}
                                >
                                    Date{sortMark(sortKey === 'date', sortDir)}
                                </button>
                            </th>
                            <th rowSpan={2}>
                                <button
                                    type="button"
                                    className="insightsSortTh"
                                    onClick={() => onSort('opponent')}
                                >
                                    Opponent{sortMark(sortKey === 'opponent', sortDir)}
                                </button>
                            </th>
                            <th rowSpan={2}>
                                <button
                                    type="button"
                                    className="insightsSortTh"
                                    onClick={() => onSort('result')}
                                >
                                    Res{sortMark(sortKey === 'result', sortDir)}
                                </button>
                            </th>
                            <th rowSpan={2}>
                                <button
                                    type="button"
                                    className="insightsSortTh"
                                    onClick={() => onSort('score')}
                                >
                                    Score{sortMark(sortKey === 'score', sortDir)}
                                </button>
                            </th>
                            <th colSpan={4}>Support</th>
                            <th className="insightsColDivider" colSpan={4}>
                                Exclusions
                            </th>
                        </tr>
                        <tr>
                            <SortTh
                                label="As"
                                active={sortKey === 'assists'}
                                dir={sortDir}
                                onClick={() => onSort('assists')}
                            />
                            <SortTh
                                label="Bl"
                                active={sortKey === 'blocks'}
                                dir={sortDir}
                                onClick={() => onSort('blocks')}
                            />
                            <SortTh
                                label="St"
                                active={sortKey === 'steals'}
                                dir={sortDir}
                                onClick={() => onSort('steals')}
                            />
                            <SortTh
                                label="TO"
                                active={sortKey === 'turnovers'}
                                dir={sortDir}
                                onClick={() => onSort('turnovers')}
                            />
                            <SortTh
                                label="ExD"
                                active={sortKey === 'exD'}
                                dir={sortDir}
                                onClick={() => onSort('exD')}
                                className="insightsColDivider"
                            />
                            <SortTh
                                label="ExC"
                                active={sortKey === 'exC'}
                                dir={sortDir}
                                onClick={() => onSort('exC')}
                            />
                            <SortTh
                                label="Net"
                                active={sortKey === 'exNet'}
                                dir={sortDir}
                                onClick={() => onSort('exNet')}
                            />
                            <SortTh
                                label="D/C"
                                active={sortKey === 'exRatio'}
                                dir={sortDir}
                                onClick={() => onSort('exRatio')}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((line) => (
                            <tr key={line.matchId}>
                                <td>
                                    {line.startDate
                                        ? moment(line.startDate).format('DD MMM YY')
                                        : '-'}
                                </td>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={matchHref(line.matchId)}
                                    >
                                        {line.isHome ? 'vs' : 'at'} {line.opponentName}
                                    </Link>
                                </td>
                                <td>{line.result || '-'}</td>
                                <td>{formatLineScore(line)}</td>
                                <td>{line.board.assists}</td>
                                <td>{line.board.blocks}</td>
                                <td>{line.board.steals}</td>
                                <td>{line.board.turnovers}</td>
                                <td className="insightsColDivider">{line.board.exclusionsDrawn}</td>
                                <td>{line.board.exclusionsCommitted}</td>
                                <td>
                                    {exclusionNet(
                                        line.board.exclusionsDrawn,
                                        line.board.exclusionsCommitted
                                    )}
                                </td>
                                <td>
                                    {exclusionRatioText(
                                        line.board.exclusionsDrawn,
                                        line.board.exclusionsCommitted
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

type ExKey = 'name' | 'matches' | 'exD' | 'exC' | 'exNet' | 'exRatio';

export const TwExclusionsBoard: React.FC<{
    rows: TwPlayerBoardRow[];
    cell: (total: number, matches: number) => string;
}> = ({ rows, cell }) => {
    const [sortKey, sortDir, onSort] = useSortState<ExKey>('exD');
    if (rows.length === 0) return null;

    const sorted = [...rows].sort((a, b) => {
        let cmp = 0;
        if (sortKey === 'name') cmp = a.name.localeCompare(b.name);
        else if (sortKey === 'matches') cmp = a.matches - b.matches;
        else if (sortKey === 'exD') cmp = a.exclusionsDrawn - b.exclusionsDrawn;
        else if (sortKey === 'exC') cmp = a.exclusionsCommitted - b.exclusionsCommitted;
        else if (sortKey === 'exNet') {
            cmp =
                exclusionNet(a.exclusionsDrawn, a.exclusionsCommitted) -
                exclusionNet(b.exclusionsDrawn, b.exclusionsCommitted);
        } else {
            const ra =
                a.exclusionsCommitted > 0
                    ? a.exclusionsDrawn / a.exclusionsCommitted
                    : a.exclusionsDrawn;
            const rb =
                b.exclusionsCommitted > 0
                    ? b.exclusionsDrawn / b.exclusionsCommitted
                    : b.exclusionsDrawn;
            cmp = ra - rb;
        }
        if (cmp !== 0) return cmp * sortDir;
        return a.name.localeCompare(b.name);
    });

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Exclusions</h2>
                <span>ExD · ExC · Net · D/C</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable">
                    <thead>
                        <tr>
                            <SortTh
                                label="Player"
                                active={sortKey === 'name'}
                                dir={sortDir}
                                onClick={() => onSort('name')}
                            />
                            <SortTh
                                label="MP"
                                active={sortKey === 'matches'}
                                dir={sortDir}
                                onClick={() => onSort('matches')}
                            />
                            <SortTh
                                label="ExD"
                                active={sortKey === 'exD'}
                                dir={sortDir}
                                onClick={() => onSort('exD')}
                            />
                            <SortTh
                                label="ExC"
                                active={sortKey === 'exC'}
                                dir={sortDir}
                                onClick={() => onSort('exC')}
                            />
                            <SortTh
                                label="Net"
                                active={sortKey === 'exNet'}
                                dir={sortDir}
                                onClick={() => onSort('exNet')}
                            />
                            <SortTh
                                label="D/C"
                                active={sortKey === 'exRatio'}
                                dir={sortDir}
                                onClick={() => onSort('exRatio')}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((row) => (
                            <tr key={row.playerId}>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={playerHref(row.playerId)}
                                    >
                                        {row.name}
                                    </Link>
                                </td>
                                <td>{row.matches}</td>
                                <td>{cell(row.exclusionsDrawn, row.matches)}</td>
                                <td>{cell(row.exclusionsCommitted, row.matches)}</td>
                                <td>
                                    {exclusionNet(row.exclusionsDrawn, row.exclusionsCommitted)}
                                </td>
                                <td>
                                    {exclusionRatioText(
                                        row.exclusionsDrawn,
                                        row.exclusionsCommitted
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

type MatchExKey = 'date' | 'opponent' | 'result' | 'score' | 'exD' | 'exC' | 'exNet' | 'exRatio';

export const TwMatchExclusionsBoard: React.FC<MatchBoardProps> = ({ lines }) => {
    const [sortKey, sortDir, onSort] = useSortState<MatchExKey>('date', -1);
    if (lines.length === 0) return null;

    const sorted = [...lines].sort((a, b) => {
        let cmp = 0;
        if (sortKey === 'date') {
            cmp =
                (a.startDate ? new Date(a.startDate).getTime() : 0) -
                (b.startDate ? new Date(b.startDate).getTime() : 0);
        } else if (sortKey === 'opponent') cmp = a.opponentName.localeCompare(b.opponentName);
        else if (sortKey === 'result') cmp = resultCmp(a, b);
        else if (sortKey === 'score') cmp = scoreCmp(a, b);
        else if (sortKey === 'exD') cmp = a.board.exclusionsDrawn - b.board.exclusionsDrawn;
        else if (sortKey === 'exC') cmp = a.board.exclusionsCommitted - b.board.exclusionsCommitted;
        else if (sortKey === 'exNet') {
            cmp =
                exclusionNet(a.board.exclusionsDrawn, a.board.exclusionsCommitted) -
                exclusionNet(b.board.exclusionsDrawn, b.board.exclusionsCommitted);
        } else {
            const ra =
                a.board.exclusionsCommitted > 0
                    ? a.board.exclusionsDrawn / a.board.exclusionsCommitted
                    : a.board.exclusionsDrawn;
            const rb =
                b.board.exclusionsCommitted > 0
                    ? b.board.exclusionsDrawn / b.board.exclusionsCommitted
                    : b.board.exclusionsDrawn;
            cmp = ra - rb;
        }
        if (cmp !== 0) return cmp * sortDir;
        return a.opponentName.localeCompare(b.opponentName);
    });

    return (
        <section className="insightsPanel">
            <div className="insightsPanelHead">
                <h2>Match · Exclusions</h2>
                <span>ExD · ExC · Net · D/C</span>
            </div>
            <div className="insightsTableScroll">
                <table className="insightsPlayerTable insightsPlayerTableWide insightsBoardTable">
                    <thead>
                        <tr>
                            <SortTh
                                label="Date"
                                active={sortKey === 'date'}
                                dir={sortDir}
                                onClick={() => onSort('date')}
                            />
                            <SortTh
                                label="Opponent"
                                active={sortKey === 'opponent'}
                                dir={sortDir}
                                onClick={() => onSort('opponent')}
                            />
                            <SortTh
                                label="Res"
                                active={sortKey === 'result'}
                                dir={sortDir}
                                onClick={() => onSort('result')}
                            />
                            <SortTh
                                label="Score"
                                active={sortKey === 'score'}
                                dir={sortDir}
                                onClick={() => onSort('score')}
                            />
                            <SortTh
                                label="ExD"
                                active={sortKey === 'exD'}
                                dir={sortDir}
                                onClick={() => onSort('exD')}
                            />
                            <SortTh
                                label="ExC"
                                active={sortKey === 'exC'}
                                dir={sortDir}
                                onClick={() => onSort('exC')}
                            />
                            <SortTh
                                label="Net"
                                active={sortKey === 'exNet'}
                                dir={sortDir}
                                onClick={() => onSort('exNet')}
                            />
                            <SortTh
                                label="D/C"
                                active={sortKey === 'exRatio'}
                                dir={sortDir}
                                onClick={() => onSort('exRatio')}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((line) => (
                            <tr key={line.matchId}>
                                <td>
                                    {line.startDate
                                        ? moment(line.startDate).format('DD MMM YY')
                                        : '-'}
                                </td>
                                <td>
                                    <Link
                                        className="insightsMatchLink"
                                        to={matchHref(line.matchId)}
                                    >
                                        {line.isHome ? 'vs' : 'at'} {line.opponentName}
                                    </Link>
                                </td>
                                <td>{line.result || '-'}</td>
                                <td>{formatLineScore(line)}</td>
                                <td>{line.board.exclusionsDrawn}</td>
                                <td>{line.board.exclusionsCommitted}</td>
                                <td>
                                    {exclusionNet(
                                        line.board.exclusionsDrawn,
                                        line.board.exclusionsCommitted
                                    )}
                                </td>
                                <td>
                                    {exclusionRatioText(
                                        line.board.exclusionsDrawn,
                                        line.board.exclusionsCommitted
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
};
